/**
 * Fase 6 (2026-07-01) — Pantalla de operación por tanda segmentada.
 *
 * Lista los pedidos activos cuya operación tiene
 * `flag_pedido_segmentado=1` y permite operar sus tandas DÍA y NOCHE
 * de forma independiente. Los pedidos sin flag no aparecen aquí (se
 * operan con la pantalla clásica de Cocina).
 *
 * Convive con la pantalla /cocina existente sin romperla — es una vista
 * adicional para el flujo Fase 6. Chef/personal_cocina elige el modo.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Sun, Moon, RefreshCw, Loader2 } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import PanelTandasPedido, {
  type FiltroSegmento,
} from '../components/cocina/PanelTandasPedido';
import type {
  TandaSnapshot,
  EstadoSegmento,
} from '../services/cocinaTandaService';
import { api } from '../services/api';

interface PedidoSegmentado {
  id: number;
  codigo: string;
  estado: string;
  fecha_entrega: string;
  cliente_nombre: string | null;
  operacion_nombre: string | null;
}

/**
 * Lista los pedidos activos de operaciones con flag_pedido_segmentado=1.
 * Filtra estados operativos (no borrador, no cancelado, no despachado).
 */
async function listarPedidosSegmentados(): Promise<PedidoSegmentado[]> {
  const r = await api.get('/pedidos-nuevos/listar', {
    params: {
      // Backend acepta filtros; este endpoint devuelve todos y
      // filtramos en el frontend por estado + flag.
      limit: 500,
    },
  });
  const raw = (r.data?.data as unknown) ?? [];
  const items = Array.isArray(raw)
    ? (raw as Array<{
        id: number;
        codigo: string;
        estado: string;
        fecha_entrega: string;
        cliente_nombre?: string | null;
        operacion_nombre?: string | null;
        operacion_flag_pedido_segmentado?: number;
        flag_pedido_segmentado?: number;
      }>)
    : [];
  return items
    .filter(
      (p) =>
        (p.operacion_flag_pedido_segmentado === 1 ||
          p.flag_pedido_segmentado === 1) &&
        !['borrador', 'cancelado', 'despachado'].includes(p.estado)
    )
    .map((p) => ({
      id: p.id,
      codigo: p.codigo,
      estado: p.estado,
      fecha_entrega: p.fecha_entrega,
      cliente_nombre: p.cliente_nombre ?? null,
      operacion_nombre: p.operacion_nombre ?? null,
    }));
}

/**
 * Estilos por estado del segmento (mismo mapa que en PanelTandasPedido,
 * usado aquí para renderizar los chips del header sin depender de
 * `pedidos.estado` que puede quedar rezagada frente a las tandas.
 */
const ESTADO_SEG_LABEL: Record<
  EstadoSegmento | 'sin_tanda',
  { text: string; color: string }
> = {
  solicitado: {
    text: 'Solicitado',
    color: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  },
  en_preparacion: {
    text: 'En preparación',
    color: 'bg-amber-200 text-amber-900 dark:bg-amber-700 dark:text-amber-100',
  },
  listo: {
    text: 'Listo',
    color:
      'bg-emerald-200 text-emerald-900 dark:bg-emerald-700 dark:text-emerald-100',
  },
  entregado_a_despacho: {
    text: 'En despacho',
    color: 'bg-blue-200 text-blue-900 dark:bg-blue-700 dark:text-blue-100',
  },
  despachado: {
    text: 'Despachado',
    color: 'bg-emerald-500 text-white dark:bg-emerald-600',
  },
  cancelado: {
    text: 'Cancelado',
    color: 'bg-red-200 text-red-900 dark:bg-red-700 dark:text-red-100',
  },
  sin_tanda: {
    text: 'Sin tanda',
    color:
      'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400 italic',
  },
};

/**
 * Deriva un chip resumen del pedido a partir de las tandas operativas.
 * Muestra AMBOS estados por separado ("Día X · Noche Y") sin sintetizar
 * a un dominante ambiguo — el operador de cocina necesita ver el estado
 * real de cada tanda, no una etiqueta que le mienta cuando divergen.
 * Cuando los dos convergen, se colapsa a un único label ("Ambas: X").
 */
function resumenTandasPedido(
  tandas: TandaSnapshot[] | undefined
): { text: string; color: string; leyenda: string } | null {
  if (!tandas || tandas.length === 0) return null;
  const dia = tandas.find((t) => t.segmento === 'dia');
  const noche = tandas.find((t) => t.segmento === 'noche');
  if (!dia && !noche) return null;
  const labelDia = dia
    ? ESTADO_SEG_LABEL[dia.estado_seg] ?? ESTADO_SEG_LABEL.sin_tanda
    : ESTADO_SEG_LABEL.sin_tanda;
  const labelNoche = noche
    ? ESTADO_SEG_LABEL[noche.estado_seg] ?? ESTADO_SEG_LABEL.sin_tanda
    : ESTADO_SEG_LABEL.sin_tanda;
  const mismo = dia && noche && dia.estado_seg === noche.estado_seg;
  const text = mismo
    ? `Día y Noche · ${labelDia.text}`
    : `Día ${labelDia.text} · Noche ${labelNoche.text}`;
  // Cuando divergen usamos un color neutro para no vender ninguno de los
  // dos estados como "el estado del pedido"; cuando convergen usamos el
  // color del label unificado.
  const color = mismo
    ? labelDia.color
    : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200 ring-1 ring-slate-300 dark:ring-slate-600';
  return { text, color, leyenda: text };
}

export default function CocinaTandas() {
  const [pedidos, setPedidos] = useState<PedidoSegmentado[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [filtroSegmento, setFiltroSegmento] = useState<FiltroSegmento>('todos');
  const [tandasPorPedido, setTandasPorPedido] = useState<
    Record<number, TandaSnapshot[]>
  >({});

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listarPedidosSegmentados();
      setPedidos(data);
    } catch (err) {
      const anyErr = err as { response?: { data?: { error?: string } } };
      setError(anyErr?.response?.data?.error ?? 'No se pudo cargar la lista');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar, refreshCounter]);

  const toggleFiltro = useCallback((seg: 'dia' | 'noche') => {
    setFiltroSegmento((prev) => (prev === seg ? 'todos' : seg));
  }, []);

  const filtroLabel = useMemo(() => {
    switch (filtroSegmento) {
      case 'dia':
        return 'Mostrando solo tandas de DÍA';
      case 'noche':
        return 'Mostrando solo tandas de NOCHE';
      default:
        return 'Mostrando ambas tandas';
    }
  }, [filtroSegmento]);

  const registrarSnapshot = useCallback(
    (pedidoId: number, tandas: TandaSnapshot[]) => {
      setTandasPorPedido((prev) => {
        const previo = prev[pedidoId];
        if (
          previo &&
          previo.length === tandas.length &&
          previo.every(
            (t, i) =>
              t.segmento === tandas[i].segmento &&
              t.estado_seg === tandas[i].estado_seg &&
              t.n_registros === tandas[i].n_registros
          )
        ) {
          return prev;
        }
        return { ...prev, [pedidoId]: tandas };
      });
    },
    []
  );

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <PageHeader
        title="Cocina · Tandas segmentadas"
        subtitle="Opera los pedidos día y noche como servicios independientes"
      />

      <div
        className="flex items-center gap-3 mb-3 flex-wrap"
        role="toolbar"
        aria-label="Filtro de segmento"
      >
        <button
          type="button"
          onClick={() => toggleFiltro('dia')}
          aria-pressed={filtroSegmento === 'dia'}
          data-testid="filtro-dia"
          className={`inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full ring-1 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-amber-400 ${
            filtroSegmento === 'dia'
              ? 'bg-amber-500 text-white ring-amber-600 shadow shadow-amber-500/30'
              : 'bg-amber-100/70 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 ring-amber-200 dark:ring-amber-800 hover:bg-amber-200'
          }`}
        >
          <Sun className="w-3.5 h-3.5" />
          Día
        </button>
        <button
          type="button"
          onClick={() => toggleFiltro('noche')}
          aria-pressed={filtroSegmento === 'noche'}
          data-testid="filtro-noche"
          className={`inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full ring-1 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-400 ${
            filtroSegmento === 'noche'
              ? 'bg-indigo-600 text-white ring-indigo-700 shadow shadow-indigo-600/30'
              : 'bg-indigo-100/70 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 ring-indigo-200 dark:ring-indigo-800 hover:bg-indigo-200'
          }`}
        >
          <Moon className="w-3.5 h-3.5" />
          Noche
        </button>
        {filtroSegmento !== 'todos' ? (
          <button
            type="button"
            onClick={() => setFiltroSegmento('todos')}
            data-testid="filtro-limpiar"
            className="text-xs text-gray-600 dark:text-gray-300 hover:underline"
          >
            Limpiar filtro
          </button>
        ) : null}
        <span
          className="text-[11px] text-gray-500 dark:text-gray-400 italic"
          data-testid="filtro-label"
        >
          {filtroLabel}
        </span>
        <button
          type="button"
          onClick={() => setRefreshCounter((c) => c + 1)}
          className="ml-auto inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition"
        >
          <RefreshCw className="w-4 h-4" /> Refrescar
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando pedidos
          segmentados…
        </div>
      ) : error ? (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-200 rounded-lg">
          {error}
        </div>
      ) : pedidos.length === 0 ? (
        <div className="text-center py-14 border border-dashed rounded-xl text-gray-500 dark:text-gray-400">
          <p className="mb-1">Sin pedidos segmentados activos.</p>
          <p className="text-xs">
            Los pedidos aparecen aquí cuando su operación tiene el flag
            día/noche encendido y están en estado{' '}
            <code>solicitado</code> / <code>en_preparacion</code> /{' '}
            <code>listo</code>.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {pedidos.map((p) => {
            const tandas = tandasPorPedido[p.id];
            const resumen = resumenTandasPedido(tandas);
            const estadoDia = tandas?.find((t) => t.segmento === 'dia');
            const estadoNoche = tandas?.find((t) => t.segmento === 'noche');
            const chipDia = estadoDia
              ? ESTADO_SEG_LABEL[estadoDia.estado_seg] ??
                ESTADO_SEG_LABEL.sin_tanda
              : ESTADO_SEG_LABEL.sin_tanda;
            const chipNoche = estadoNoche
              ? ESTADO_SEG_LABEL[estadoNoche.estado_seg] ??
                ESTADO_SEG_LABEL.sin_tanda
              : ESTADO_SEG_LABEL.sin_tanda;
            return (
              <div
                key={p.id}
                className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-5"
                data-testid={`pedido-seg-${p.id}`}
              >
                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-2">
                  <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {p.codigo}
                  </span>
                  {p.cliente_nombre ? (
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {p.cliente_nombre}
                      {p.operacion_nombre ? ` · ${p.operacion_nombre}` : ''}
                    </span>
                  ) : null}
                  {resumen ? (
                    <span
                      className={`ml-auto text-xs px-2 py-0.5 rounded-full font-semibold ${resumen.color}`}
                      data-testid={`pedido-${p.id}-resumen-tandas`}
                      title={resumen.leyenda}
                    >
                      {resumen.text}
                    </span>
                  ) : (
                    <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                      Cargando tandas…
                    </span>
                  )}
                  <span className="text-xs text-gray-500">
                    Entrega {p.fecha_entrega?.slice(0, 10) ?? '—'}
                  </span>
                </div>
                <div
                  className="flex flex-wrap gap-2 mb-4"
                  data-testid={`pedido-${p.id}-chips-segmentos`}
                >
                  <span
                    className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${chipDia.color}`}
                    data-testid={`pedido-${p.id}-chip-dia`}
                  >
                    <Sun className="w-3 h-3" /> Día · {chipDia.text}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${chipNoche.color}`}
                    data-testid={`pedido-${p.id}-chip-noche`}
                  >
                    <Moon className="w-3 h-3" /> Noche · {chipNoche.text}
                  </span>
                </div>
                <PanelTandasPedido
                  pedidoId={p.id}
                  pedidoCodigo={p.codigo}
                  filtroSegmento={filtroSegmento}
                  onTandasSnapshot={(t) => registrarSnapshot(p.id, t)}
                  onTandaCambio={() => setRefreshCounter((c) => c + 1)}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
